<script lang="ts">
  import { keyboard } from '../keys/keyboard.svelte';
  import { truncatedTitle } from '../logic/truncatedTitle';
  import type { DiffModel, FileEntry } from '../state/diffs.svelte';
  import LoadError from './LoadError.svelte';
  import type { PrStore } from '../state/pr.svelte';
  import type { RepoTree } from '../state/repotree.svelte';
  import { buildTree, basename, dirname, fileIcon, treeOrder, type TreeNode } from '../logic/tree';
  import { CATEGORY_LABELS, type Category } from '../logic/categorize';
  import { progressSummary, reviewProgress, subtreeDiff } from '../logic/browse';
  import type { FileFilter, TypeClassifier, TypeCount } from '../logic/filefilter';
  import TypeChips from './TypeChips.svelte';
  import ViewedMark from './ViewedMark.svelte';
  import { prefs } from '../state/prefs.svelte';
  import { tick, untrack } from 'svelte';

  interface Props {
    pr: PrStore;
    model: DiffModel;
    /** Beside another comparison than base → head: the whole pull request's review progress, null until known; undefined in base → head. */
    whole?: { done: number; total: number; exact: boolean } | null;
    visible: string[];
    selected: string | null;
    view: 'tree' | 'list';
    unreviewedOnly: boolean;
    category: Category | 'all';
    unresolvedByPath: Record<string, number>;
    /** Changed files only, or every file of the repository at the head. */
    scope?: 'changed' | 'all';
    /** The PR's changed files. */
    changed?: Set<string>;
    /** The repository tree ("All files"). */
    tree?: RepoTree | null;
    /** Folders of a lazily loaded tree whose files are not known yet. */
    dirs?: string[];
    /** A search is active (folders open to show matches). */
    searching?: boolean;
    /** The file filter (type chips, path pattern) from the URL. */
    filter?: FileFilter | null;
    /** Type chips for the current scope. */
    types?: TypeCount[];
    classifier?: TypeClassifier | null;
    /** The open file, when the file filter hides it: listed, marked as outside the filter. */
    outside?: string | null;
    ontypes?: (selected: string[]) => void;
    onscope?: (v: 'changed' | 'all') => void;
    onselect: (path: string) => void;
    /** Hides the explorer column; no button when absent. */
    onhide?: (() => void) | null;
    onview: (v: 'tree' | 'list') => void;
    onunreviewed: (v: boolean) => void;
    oncategory: (c: Category | 'all') => void;
    /**
     * Mark every changed line of `paths` reviewed (true) or clear their
     * marks; resolves to how many files were changed. Without it, files
     * cannot be selected together.
     */
    onmark?: ((paths: string[], reviewed: boolean) => Promise<number>) | null;
    /** Why review marks cannot be taken here, or null. */
    marksOffText?: string | null;
  }
  let {
    pr,
    model,
    whole = undefined,
    visible,
    selected,
    view,
    unreviewedOnly,
    category,
    unresolvedByPath,
    scope = 'changed',
    changed = new Set<string>(),
    tree: repoTree = null,
    dirs = [],
    searching = false,
    filter = null,
    types = [],
    classifier = null,
    outside = null,
    ontypes = () => {},
    onscope = () => {},
    onhide = null,
    onselect,
    onview,
    onunreviewed,
    oncategory,
    onmark = null,
    marksOffText = null,
  }: Props = $props();

  /** Rows rendered in the flat list at most (a large repository has many thousand files). */
  const LIST_LIMIT = 1500;

  let list: HTMLDivElement | undefined = $state();
  const all = $derived(scope === 'all');
  const byPath = $derived(new Map(model.entries.map((e) => [e.path, e])));
  /** Rows: the visible files, plus the open file when the filter hides it. */
  const rows = $derived(outside && !visible.includes(outside) ? [...visible, outside] : visible);
  const tree = $derived(buildTree(rows, dirs));
  const filtered = $derived(!!filter?.active);
  const closed = $derived(pr.prefs.data.closedFolders);
  const allOpen = $derived(pr.prefs.data.allFolders ?? {});

  /** Held drafts per file (files the PR does not change have no other drafts). */
  const heldByPath = $derived.by(() => {
    const m: Record<string, number> = {};
    for (const h of pr.held) m[h.target.path] = (m[h.target.path] ?? 0) + 1;
    return m;
  });

  /** Folders holding a changed file or the selected file: open by default in "All files". */
  const defaultOpen = $derived.by(() => {
    const s = new Set<string>();
    for (const p of [...changed, ...(selected ? [selected] : [])]) for (let d = dirname(p); d && !s.has(d); d = dirname(d)) s.add(d);
    return s;
  });

  const totals = $derived.by(() => {
    const { total, done } = reviewProgress(
      model.entries.map((e) => e.path),
      (p) => model.progress(p),
      filter?.active ? filter.test : undefined,
    );
    return { total, done, pct: total ? Math.round((done / total) * 100) : 0 };
  });
  /** What the summary says: the comparison shown under its name, and beside another comparison than base → head the whole pull request's count too. */
  const summary = $derived(
    progressSummary({ shown: { done: totals.done, total: totals.total, filtered: !!filter?.active }, comparison: model.interdiff ? model.label : null, whole: whole ?? null }),
  );

  function stats(path: string) {
    return model.progress(path);
  }

  // Changed files whose rows are in view load first (`DiffModel.favor`), so
  // the review icons the reader can see fill in before the rest.
  const inView = new Set<string>();
  let favorQueued = false;
  const observer =
    typeof IntersectionObserver === 'undefined'
      ? null
      : new IntersectionObserver((records) => {
          for (const r of records) {
            const path = (r.target as HTMLElement).dataset.path;
            if (!path) continue;
            if (r.isIntersecting) inView.add(path);
            else inView.delete(path);
          }
          if (favorQueued) return;
          favorQueued = true;
          queueMicrotask(() => {
            favorQueued = false;
            model.favor([...inView]);
          });
        });
  $effect(() => () => observer?.disconnect());

  /** Watch a changed file's row for the in-view set. */
  function seen(node: HTMLElement, path: string) {
    node.dataset.path = path;
    observer?.observe(node);
    return {
      update(next: string) {
        inView.delete(path);
        path = next;
        node.dataset.path = next;
      },
      destroy() {
        inView.delete(path);
        observer?.unobserve(node);
      },
    };
  }

  function folderTally(node: TreeNode) {
    let total = 0;
    let done = 0;
    let changedCount = 0;
    const files = treeOrder(node);
    const diffed: string[] = [];
    for (const p of files) {
      if (!changed.has(p)) continue;
      changedCount++;
      const s = stats(p);
      total += s.total;
      done += s.done;
      if (!byPath.get(p)?.noise) diffed.push(p);
    }
    return { total, done, count: files.length, changed: changedCount, diff: subtreeDiff(diffed, (p) => model.counts(p)) };
  }

  function isOpen(node: TreeNode): boolean {
    if (!all) return !closed[node.path];
    const v = allOpen[node.path];
    if (v !== undefined) return v;
    return searching || defaultOpen.has(node.path);
  }

  function setOpen(node: TreeNode, value: boolean) {
    if (value === isOpen(node)) return;
    if (!all) {
      setClosed(node.path, !value);
      return;
    }
    pr.prefs.data.allFolders = { ...allOpen, [node.path]: value };
    pr.prefs.save();
    // A compressed chain ("a/b/c") may pass folders not loaded yet: load every level.
    if (value) void repoTree?.reveal(node.path + '/');
  }

  function setClosed(path: string, value: boolean) {
    pr.prefs.data.closedFolders[path] = value;
    pr.prefs.save();
  }

  function allFolders(node: TreeNode): TreeNode[] {
    return node.folders.flatMap((f) => [f, ...allFolders(f)]);
  }

  function setAll(open: boolean) {
    if (!all) {
      allFolders(tree).forEach((f) => setClosed(f.path, !open));
      return;
    }
    // Expanding a lazily loaded tree loads only folders already known.
    const next = { ...allOpen };
    for (const f of allFolders(tree)) if (!open || repoTree?.dirState(f.path) !== 'unloaded') next[f.path] = open;
    pr.prefs.data.allFolders = next;
    pr.prefs.save();
  }

  /** Open the active file's folders and scroll it into view. */
  export async function reveal() {
    if (!selected) return;
    if (all) {
      const next = { ...allOpen };
      let changedAny = false;
      for (let d = dirname(selected); d; d = dirname(d))
        if (next[d] === false) {
          delete next[d];
          changedAny = true;
        }
      if (changedAny) {
        pr.prefs.data.allFolders = next;
        pr.prefs.save();
      }
    } else for (let d = dirname(selected); d; d = dirname(d)) if (closed[d]) setClosed(d, false);
    await tick();
    const row = list?.querySelector<HTMLElement>('.explorer-file.active');
    if (!row || !list) return;
    const offset = row.getBoundingClientRect().top - list.getBoundingClientRect().top;
    if (offset < 0 || offset + row.offsetHeight > list.clientHeight) list.scrollTop += offset - 8;
  }

  // Reveal on a new selection only: reveal() reads the folders' open state,
  // and tracking that would reopen a folder the moment it is closed.
  $effect(() => {
    void selected;
    untrack(() => void reveal());
  });

  // ---------------------------------------------------------------- several files
  // Ctrl/⌘-click adds a changed file to the selection (or takes it out),
  // Shift-click selects the run from the last one clicked (or the open
  // file), Ctrl/⌘-click on a folder selects its changed files; the bar
  // then marks them all. A plain click opens a file and drops the selection.
  let picked: Set<string> = $state(new Set());
  let pickAnchor: string | null = null;
  let marking = $state(false);
  /** The selected files the list still shows, in its order. */
  const pickedShown = $derived(rows.filter((p) => picked.has(p) && byPath.has(p)));
  /** The changed files in the order the list shows them (folders' closed contents excluded). */
  const shownOrder = () => [...(list?.querySelectorAll<HTMLElement>('.explorer-file[data-path]') ?? [])].map((el) => el.dataset.path!);

  function clickFile(e: MouseEvent, path: string) {
    if (onmark && byPath.has(path) && (e.metaKey || e.ctrlKey || e.shiftKey)) {
      e.preventDefault();
      const next = new Set(e.shiftKey && !(e.metaKey || e.ctrlKey) ? [] : picked);
      const from = pickAnchor ?? selected;
      if (e.shiftKey && from) {
        const order = shownOrder();
        const [a, b] = [order.indexOf(from), order.indexOf(path)];
        if (a >= 0 && b >= 0) for (const p of order.slice(Math.min(a, b), Math.max(a, b) + 1)) next.add(p);
        else next.add(path);
      } else if (!next.delete(path)) next.add(path);
      if (!e.shiftKey) pickAnchor = path;
      picked = next;
      return;
    }
    picked = new Set();
    pickAnchor = null;
    onselect(path);
  }

  /** Ctrl/⌘-click on a folder: its changed files the list shows join the selection (or leave it, when all of them are in it). */
  function clickFolder(e: MouseEvent, node: TreeNode) {
    if (!onmark || !(e.metaKey || e.ctrlKey)) return;
    e.preventDefault();
    const inside = rows.filter((p) => byPath.has(p) && p.startsWith(node.path + '/'));
    const next = new Set(picked);
    const every = inside.length > 0 && inside.every((p) => next.has(p));
    for (const p of inside) every ? next.delete(p) : next.add(p);
    picked = next;
  }

  async function markPicked(reviewed: boolean) {
    if (!onmark || marking || !pickedShown.length) return;
    marking = true;
    try {
      if (await onmark(pickedShown, reviewed)) picked = new Set();
    } finally {
      marking = false;
    }
  }

  const total = $derived(all ? new Set([...(repoTree?.paths ?? []), ...changed]).size : model.entries.length);
  const foot = $derived(model.totals());
</script>

{#snippet fileRow(path: string, flat: boolean)}
  {#if byPath.has(path)}
    {@render changedRow(path, flat)}
  {:else}
    {@const icon = fileIcon(path)}
    <button
      class="explorer-file unchanged"
      class:active={path === selected}
      class:outside={path === outside}
      aria-current={path === selected ? 'true' : undefined}
      title="{path} · not changed by this pull request{path === outside ? ' · outside the filter' : ''}"
      onclick={() => onselect(path)}
    >
      <span class="explorer-file-main">
        <span class="language-icon lang-{icon[1]}">{icon[0]}</span>
        <span class="explorer-filename">{basename(path)}</span>
        {#if path === outside}<span class="outside-tag">outside filter</span>{/if}
        {#if heldByPath[path]}<span class="file-held" title="Your held drafts">✎ {heldByPath[path]}</span>{/if}
      </span>
      {#if flat}<span class="flat-path">{dirname(path) || '/'}</span>{/if}
    </button>
  {/if}
{/snippet}

{#snippet changedRow(path: string, flat: boolean)}
  {@const e = byPath.get(path) as FileEntry}
  {@const s = stats(path)}
  {@const counts = model.counts(path)}
  {@const done = s.exact && s.done === s.total}
  {@const icon = fileIcon(path)}
  {@const status = e.status === 'added' ? ['A', 'new', 'New file'] : e.status === 'removed' ? ['D', 'removed', 'Deleted file'] : e.status === 'renamed' ? ['R', 'modified', 'Renamed'] : ['M', 'modified', 'Modified file']}
  <button
    class="explorer-file"
    class:active={path === selected}
    class:noise={e.noise}
    class:outside={path === outside}
    aria-current={path === selected ? 'true' : undefined}
    class:picked={picked.has(path)}
    data-review={s.exact ? 'known' : 'estimate'}
    data-path={path}
    use:seen={path}
    aria-pressed={picked.size ? picked.has(path) : undefined}
    title="{path} · {s.done}/{s.total} lines reviewed{path === outside ? ' · outside the filter' : ''}{onmark ? ' · Ctrl/⌘-click or Shift-click to select several' : ''}"
    onclick={(e) => clickFile(e, path)}
  >
    <span class="explorer-file-main">
      <span class="language-icon lang-{icon[1]}">{icon[0]}</span>
      <span class="explorer-filename">{basename(path)}</span>
      {#if path === outside}<span class="outside-tag">outside filter</span>{/if}
      <span class="change-status {status[1]}" title={status[2]}>{status[0]}</span>
      <span class="file-review-circle" class:complete={done} style="--completion:{s.total ? (s.done / s.total) * 360 : 0}deg"
        >{done ? '✓' : ''}</span
      >
      <ViewedMark state={pr.viewed.stateOf(path)} />
    </span>
    {#if flat}<span class="flat-path">{dirname(path) || '/'}</span>{/if}
    <span class="explorer-file-meta">
      {#if e.noise}<span class="muted">rebase</span>{:else if counts}
        <span class="positive">+{counts.added}</span><span class="negative">−{counts.removed}</span>
        <span class="file-spark"><i style="width:{(counts.added / (counts.added + counts.removed || 1)) * 100}%"></i></span>
      {:else}
        <span class="muted" title="Counted once this file's diff is computed">…</span>
      {/if}
      {#if unresolvedByPath[path]}<span class="file-threads" title="Unresolved threads">● {unresolvedByPath[path]}</span>{/if}
      {#if heldByPath[path]}<span class="file-held" title="Your held drafts">✎ {heldByPath[path]}</span>{/if}
      <span class="file-review-count">{s.done}/{s.exact ? s.total : '~' + s.total}</span>
    </span>
  </button>
{/snippet}

{#snippet folder(node: TreeNode)}
  {@const t = folderTally(node)}
  {@const complete = t.total > 0 && t.done === t.total}
  {@const open = isOpen(node)}
  {@const state = repoTree && all ? repoTree.dirState(node.path) : 'loaded'}
  {@const holds = !!selected && selected.startsWith(node.path + '/')}
  <details
    class="explorer-folder"
    class:contains-active={holds}
    class:holds-active={holds && !open}
    class:no-changes={all && !t.changed}
    {open}
    ontoggle={(e) => setOpen(node, (e.currentTarget as HTMLDetailsElement).open)}
  >
    <summary use:truncatedTitle={{ text: node.path, target: '.folder-name' }} onclick={(e) => clickFolder(e, node)}>
      <span class="folder-chevron">›</span>
      <svg class="folder-icon" viewBox="0 0 20 16" aria-hidden="true"><path d="M1 3V1h6l2 2h10v12H1Z" /></svg>
      <span class="folder-name">{node.name}</span>
      {#if holds && !open && selected}<span class="folder-selected" title="Selected: {selected}">↳ {basename(selected)}</span>{/if}
      <span class="folder-count">{state === 'unloaded' ? '' : state === 'loading' ? '…' : t.count}</span>
      {#if t.diff}
        <span class="folder-diff" title={t.diff.complete ? 'Lines added and removed below this folder' : 'Lines added and removed so far; some files are not counted yet'}
          ><span class="positive">+{t.diff.added}</span><span class="negative">−{t.diff.removed}</span>{#if !t.diff.complete}<span class="muted">…</span>{/if}
          <span class="file-spark"><i style="width:{(t.diff.added / (t.diff.added + t.diff.removed || 1)) * 100}%"></i></span></span
        >
      {/if}
      {#if !all || t.changed}
        <span class="folder-progress" class:complete>{complete ? '✓' : `${t.done}/${t.total}`}</span>
      {/if}
    </summary>
    {#if open}
      <div class="folder-children">
        {@render contents(node)}
        {#if state === 'loading'}<div class="folder-note">Loading…</div>{:else if state === 'error'}<div class="folder-note">Could not load this folder.</div>{/if}
      </div>
    {/if}
  </details>
{/snippet}

{#snippet contents(node: TreeNode)}
  {#each node.folders as f (f.path)}{@render folder(f)}{/each}
  {#each node.files as p (p)}{@render fileRow(p, false)}{/each}
{/snippet}

<aside class="explorer" id="file-list" aria-label={all ? 'Repository files' : 'Changed files'}>
  <div class="explorer-heading">
    <div class="eyebrow">{pr.owner}/{pr.repo} / PR {pr.number}</div>
    <div class="explorer-title">
      <strong>{all ? 'Files' : 'Changes'}</strong><span class="file-count">{visible.length}</span>{#if onhide}<button type="button" class="explorer-hide" onclick={onhide} title="Hide the file list{keyboard.hint({ kind: 'present', what: 'file-list' })}" aria-label="Hide the file list">«</button>{:else}<span class="explorer-orbit" aria-hidden="true">◈</span>{/if}
    </div>
    <!-- Three rows in every comparison (the last one empty in base → head), so the list below never moves when the comparison changes. -->
    <div class="explorer-counts" role="group" aria-label="Review progress">
      <span class="sr-only">{summary.spoken}</span>
      <div class="explorer-scope" aria-hidden="true" title="A line counts as reviewed when a review mark covers it, whichever view the mark was made in; the total is the lines this comparison changes">{summary.scope}</div>
      <div class="explorer-summary" aria-hidden="true">
        <span>{summary.figure}{#if filtered}<span class="filtered-tag" title="Counts only the changed files the type and path filters keep">{' · filtered'}</span>{/if}</span><span class="pct">{totals.pct}%</span>
      </div>
      <div class="explorer-progress" aria-hidden="true"><i style="width:{totals.pct}%"></i></div>
      <div class="explorer-whole" aria-hidden="true" title={summary.whole ? 'The pull request\'s own comparison (base → head), under the same marks' : undefined}>{summary.whole ?? ''}</div>
    </div>
    <div class="explorer-tools">
      <div class="explorer-segment" role="group" aria-label="Files to list">
        <button class:chosen={!all} aria-pressed={!all} title="Files this pull request changes" onclick={() => onscope('changed')}>Changed</button>
        <button class:chosen={all} aria-pressed={all} title="Every file of the repository at the pull request's head" onclick={() => onscope('all')}>All files</button>
      </div>
      <div class="explorer-segment">
        <button class:chosen={view === 'tree'} aria-pressed={view === 'tree'} title="Folder tree" onclick={() => onview('tree')}>Tree</button>
        <button class:chosen={view === 'list'} aria-pressed={view === 'list'} title="Flat file list" onclick={() => onview('list')}>List</button>
      </div>
      <button class:chosen={unreviewedOnly} aria-pressed={unreviewedOnly} title="Focus on changed files with unreviewed lines" onclick={() => onunreviewed(!unreviewedOnly)}>Unreviewed</button>
      <select aria-label="Filter by category" value={category} onchange={(e) => oncategory((e.currentTarget as HTMLSelectElement).value as Category | 'all')}>
        <option value="all">Any kind</option>
        {#each Object.entries(CATEGORY_LABELS) as [k, label]}<option value={k}>{label}</option>{/each}
      </select>
      <button class="reveal" title="Reveal the active file" onclick={() => reveal()}>◎</button>
    </div>
    {#if classifier && (types.length || filter?.types.length)}
      <TypeChips {types} selected={filter?.types ?? []} {classifier} onchange={ontypes} />
    {/if}
    <div class="explorer-subbar">
      <span>{visible.length} of {total} files{all && changed.size ? ` · ${changed.size} changed` : ''}{filtered ? ' · filtered' : ''}</span>
      <div>
        <button disabled={view !== 'tree'} onclick={() => setAll(false)}>Collapse</button>
        <button disabled={view !== 'tree'} onclick={() => setAll(true)}>Expand</button>
      </div>
    </div>
    {#if all && repoTree}
      {#if repoTree.status === 'loading'}
        <div class="explorer-note" role="status">Loading the repository’s files…</div>
      {:else if repoTree.status === 'error'}
        {#if repoTree.failure}
          <LoadError failure={repoTree.failure} what="the repository’s files" onretry={() => repoTree.load()} />
        {/if}
      {:else if repoTree.lazy}
        <div class="explorer-note" role="note">Large repository: folders load as you open them, and search covers loaded folders.</div>
      {/if}
    {/if}
  </div>
  {#if pickedShown.length}
    <div class="explorer-picked" role="toolbar" aria-label="Selected files">
      <span><b>{pickedShown.length}</b> file{pickedShown.length === 1 ? '' : 's'} selected</span>
      <button type="button" disabled={marking || !!marksOffText} title={marksOffText ?? 'Mark every change in the selected files reviewed'} onclick={() => markPicked(true)}>Mark reviewed</button>
      <button type="button" disabled={marking || !!marksOffText} title={marksOffText ?? 'Remove the review marks of the selected files'} onclick={() => markPicked(false)}>Unmark</button>
      <button type="button" class="explorer-picked-clear" title="Clear the selection (Esc)" aria-label="Clear the selection" onclick={() => (picked = new Set())}>✕</button>
    </div>
  {/if}
  <!-- svelte-ignore a11y_no_static_element_interactions (Esc drops the selection made in the list) -->
  <div class="filelist" bind:this={list} onkeydown={(e) => e.key === 'Escape' && picked.size && (e.stopPropagation(), (picked = new Set()))}>
    {#if !rows.length && !(all && view === 'tree' && dirs.length)}
      <div class="explorer-empty">
        ⌕<b>No matching files</b>
        <span
          >{all
            ? 'File names match across the repository; file contents are searched only in changed files and files you opened.'
            : 'Try another search or clear a filter.'}</span
        >
      </div>
    {:else if view === 'tree'}
      {@render contents(tree)}
    {:else}
      {#each rows.slice(0, LIST_LIMIT) as p (p)}{@render fileRow(p, true)}{/each}
      {#if rows.length > LIST_LIMIT}
        <div class="explorer-note">{(rows.length - LIST_LIMIT).toLocaleString()} more files: search to narrow the list.</div>
      {/if}
    {/if}
  </div>
  <div class="explorer-foot">
    <span title={model.interdiff ? 'Lines changed between these snapshots' : 'Lines changed in this pull request'}
      ><span class="positive">{foot.complete ? '' : '≥'}+{foot.added}</span> <span class="negative">−{foot.removed}</span></span
    >
    {#if all}
      <small>Search matches every file name; file contents only in changed files and files you opened.</small>
    {/if}
    <small>{pr.serverDataText ?? pr.marks.error ?? 'Review marks are private to you and sync across devices.'}</small>
    {#if pr.viewed.enabled && pr.viewed.paused}
      <small class="gh-viewed-error" role="status">GitHub won't let Changeling set “Viewed” here ({pr.viewed.paused}), so it is not synced until the page reloads.</small>
    {:else if pr.viewed.errors}
      <small class="gh-viewed-error" role="status"
        >{pr.viewed.errors === 1 ? '1 file' : `${pr.viewed.errors} files`} did not sync with GitHub's “Viewed”.
        <button type="button" onclick={() => pr.viewed.retryAll()}>Retry</button></small
      >
    {/if}
  </div>
</aside>
